/**
 * The bench, and the whole screen.
 *
 * Two ways in. Turning the phone sideways takes it — the sideways layout hides
 * everything of ours that is not the bench, and the browser's own bars are the
 * only thing left taking height from the one dimension that layout exists to
 * buy back. And a button, for the person who wants the bench big without
 * turning anything: from portrait that also asks the screen to turn, since a
 * bench is a wide thing and a tall window is the wrong shape for it.
 *
 * ALWAYS SOMETHING HAPPENS. iPhone Safari has never implemented
 * `Element.requestFullscreen` for a plain element — it exists for <video> and
 * nothing else — so where the real request is unavailable or refused, the page
 * is pinned over everything instead (`data-full="true"`, see the CSS). Safari's
 * own bars stay, the site's chrome does not. This is the same fallback, keyed
 * the same way, that SimulationPlayer already uses for the same reason; the two
 * should stay recognisably one behaviour.
 *
 * The rest is what the platforms allow:
 *
 * - Fullscreen needs a user gesture. The button is one. An orientation change
 *   is NOT, so the automatic request is only granted while a tap is still
 *   recent; refused for that reason, we arm the next tap on the bench, and
 *   rotating then touching anything gets there.
 * - Orientation lock needs fullscreen first, and exists in Chrome/Edge on
 *   Android. Where it is missing the button still works — you get a bench that
 *   fills the screen in the shape the phone is already held in.
 *
 * Leaving fullscreen deliberately — Escape, the swipe, the browser's own
 * button — is respected: sideways will not ask again until the bench has been
 * turned upright and back.
 *
 * Shared by every bench. Only the chemistry lab has the sideways layout the
 * automatic request is written against (`SIDEWAYS` below), so the others pass
 * `followRotation: false` and get the button alone.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** The same condition the stylesheet's landscape layout is written against;
 *  the two must agree, or the bench would go fullscreen in a layout that was
 *  not built for it. */
const SIDEWAYS = '(orientation: landscape) and (max-height: 640px)';

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: 'landscape') => Promise<void>;
};

function realFullscreenElement(): Element | null {
  const d = document as FullscreenDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

function requestOn(el: FullscreenElement): (() => Promise<void> | void) | undefined {
  const request = el.requestFullscreen ?? el.webkitRequestFullscreen;
  return request ? request.bind(el) : undefined;
}

function leaveReal(): void {
  const d = document as FullscreenDocument;
  const exit = d.exitFullscreen ?? d.webkitExitFullscreen;
  if (exit) void exit.call(d);
}

/** Ask for landscape, and don't care if the platform says no — a bench filling
 *  a portrait screen is still a bench filling the screen. */
function askForLandscape(): void {
  const orientation = window.screen?.orientation as LockableOrientation | undefined;
  if (!orientation?.lock) return;
  void orientation.lock('landscape').catch(() => {});
}

function releaseOrientation(): void {
  const orientation = window.screen?.orientation;
  if (typeof orientation?.unlock === 'function') {
    try { orientation.unlock(); } catch { /* not supported; nothing was locked */ }
  }
}

export interface BenchFullscreen {
  /** True in both kinds of fullscreen — the browser's, and the pinned
   *  fallback. The button reads this to know which way it points. */
  isFullscreen: boolean;
  /** Set on the page as `data-full`, which is what the pinned fallback is
   *  styled from. */
  pinned: boolean;
  toggle: () => void;
}

export function useBenchFullscreen(
  ref: RefObject<HTMLElement | null>,
  { followRotation = true }: { followRotation?: boolean } = {},
): BenchFullscreen {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pinned, setPinned] = useState(false);
  // Read inside listeners that outlive the render that installed them.
  const pinnedRef = useRef(false);
  const setPinnedState = useCallback((next: boolean) => {
    pinnedRef.current = next;
    setPinned(next);
    setIsFullscreen(next || realFullscreenElement() === ref.current);
  }, [ref]);

  /** Fullscreen if the browser will, pinned if it won't. */
  const enter = useCallback(async (): Promise<void> => {
    const el = ref.current as FullscreenElement | null;
    if (!el) return;
    const request = requestOn(el);
    if (!request) { setPinnedState(true); return; }
    await request();
    askForLandscape();
  }, [ref, setPinnedState]);

  const exit = useCallback((): void => {
    releaseOrientation();
    if (pinnedRef.current) setPinnedState(false);
    if (realFullscreenElement() === ref.current) leaveReal();
  }, [ref, setPinnedState]);

  // ── the button ────────────────────────────────────────────────────────
  const toggle = useCallback(() => {
    if (pinnedRef.current || realFullscreenElement()) { exit(); return; }
    // A refused request is not a dead end here: pin instead, so the button
    // always does the thing it says.
    void enter().catch(() => setPinnedState(true));
  }, [enter, exit, setPinnedState]);

  // ── turning the phone ─────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const sideways = window.matchMedia(SIDEWAYS);
    // Whether we may still ask during THIS spell of landscape. Cleared once we
    // are fullscreen, and when the person dismisses it themselves.
    let mayAsk = false;
    let armed = false;

    const tryEnter = async (): Promise<void> => {
      if (!mayAsk || pinnedRef.current || realFullscreenElement()) return;
      try {
        await enter();
        mayAsk = false;
        disarm();
      } catch {
        // Refused for want of a gesture — the next touch is one.
        arm();
      }
    };

    const onGesture = (): void => { void tryEnter(); };

    const arm = (): void => {
      if (armed) return;
      armed = true;
      // Capture, and on the events a browser counts as activation. `click`
      // alone misses a drag on the bench, which is a whole interaction that
      // would leave the bars up.
      document.addEventListener('pointerdown', onGesture, true);
      document.addEventListener('keydown', onGesture, true);
    };

    const disarm = (): void => {
      if (!armed) return;
      armed = false;
      document.removeEventListener('pointerdown', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
    };

    const onOrientation = (): void => {
      if (!followRotation) return;
      if (sideways.matches) {
        mayAsk = true;
        void tryEnter();
      } else {
        mayAsk = false;
        disarm();
        exit();
      }
    };

    const onFullscreenChange = (): void => {
      const real = realFullscreenElement() === ref.current;
      setIsFullscreen(real || pinnedRef.current);
      // Dropping out while still sideways is a decision: stop asking until the
      // bench is turned upright and back.
      if (!real && !pinnedRef.current) { mayAsk = false; disarm(); releaseOrientation(); }
    };

    onOrientation();
    sideways.addEventListener('change', onOrientation);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);

    return () => {
      sideways.removeEventListener('change', onOrientation);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      disarm();
      exit();
    };
  }, [enter, exit, ref, followRotation]);

  return { isFullscreen, pinned, toggle };
}
